import React, { useCallback, useEffect, useRef, useState } from "react";
import TIM from "@tencentcloud/chat";
import EmojiPicker from "./EmojiPicker";
import Swal from "sweetalert2";
import Cookies from "js-cookie";

interface AvChatRoomProps {
  tim: any;
  groupID: string;
  userID: string;
  userSig: string;
  startTime?: string;
  endTime?: string;
  setUser: React.Dispatch<React.SetStateAction<any>>;
}

const AvChatRoom: React.FC<AvChatRoomProps> = ({
  tim,
  groupID,
  userID,
  userSig,
  startTime,
  endTime,
  setUser,
}) => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<any[]>([]);
  const messageListRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<any[]>([]);
  const [isWithinTime, setIsWithinTime] = useState(false);

  console.log(isWithinTime);

  useEffect(() => {
    if (!startTime || !endTime) {
      setIsWithinTime(false);
      return;
    }

    const parseDate = (val: string) => {
      const parts = val.replace("T", " ").split(/[- :]/);
      const [year, month, day, hour, minute, second] = parts.map(Number);
      return new Date(
        year,
        month - 1,
        day,
        hour,
        minute,
        second || 0
      ).getTime();
    };

    const checkTime = () => {
      const now = Date.now();
      const start = parseDate(startTime);
      const end = parseDate(endTime);
      setIsWithinTime(
        !isNaN(start) && !isNaN(end) && now >= start && now <= end
      );
    };

    checkTime();
    const timer = setInterval(checkTime, 1000);
    return () => clearInterval(timer);
  }, [startTime, endTime]);

  // Keep ref in sync with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // Initialize TIM login and join group once
  useEffect(() => {
    if (!tim || !userID || !userSig || !groupID) return;

    const init = async () => {
      try {
        const loginUser = tim.getLoginUser();
        if (loginUser !== userID) {
          await tim.login({ userID, userSig });
          console.log("Logged in");
        }

        // Always attempt to join AVChatRoom (won't throw if already joined)
        const res = await tim.joinGroup({
          groupID,
          type: TIM.TYPES.GRP_AVCHATROOM,
        });

        if (res.data.status === TIM.TYPES.JOIN_STATUS_ALREADY_IN_GROUP) {
          console.log("Already in group");
        } else if (res.data.status === TIM.TYPES.JOIN_STATUS_SUCCESS) {
          console.log("Joined group successfully");
          const userCookie = Cookies.get("user");
          if (userCookie) {
            Cookies.set("user", userCookie, { expires: 1 });
          }
        } else {
          console.warn("Unexpected join status:", res.data.status);
        }
      } catch (err) {
        console.error(`Login or join failed: ${groupID}`, err);
        Cookies.remove("user");
        setUser(null);
      }
    };

    init();
  }, [tim, userID, userSig, groupID]);

  // Handle real-time message receive
  const handleMessageReceived = useCallback(
    (event: any) => {
      const relevantMessages = event.data.filter(
        (msg: any) => msg.conversationID === `GROUP${groupID}`
      );

      if (relevantMessages.length > 0) {
        setMessages([...messagesRef.current, ...relevantMessages]);
      }
    },
    [groupID]
  );

  useEffect(() => {
    tim.on(TIM.EVENT.MESSAGE_RECEIVED, handleMessageReceived);
    return () => {
      tim.off(TIM.EVENT.MESSAGE_RECEIVED, handleMessageReceived);
    };
  }, [tim, handleMessageReceived]);

  const sendMessage = async () => {
    if (!isWithinTime) {
      Swal.fire({
        title: "THẤT BẠI",
        text: "Phát sóng trực tiếp chưa bắt đầu",
        icon: "error",
        confirmButtonText: "Xác nhận",
        confirmButtonColor: "red",
      });
      setInput("");
      return;
    }

    if (!input.trim()) return;
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/LiveStream/CheckSensitiveWords?apikey=${process.env.REACT_APP_API_KEY}&content=${input}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const result = await res.json();

    if (result.data === false) {
      // console.error("Input contains a banned word!");
      Swal.fire({
        title: "THẤT BẠI",
        text: "Nội dung bạn đăng có chứa thông tin nhạy cảm",
        icon: "error",
        confirmButtonText: "Xác nhận",
        confirmButtonColor: "red",
      });
      setInput("");
    } else {
      const message = tim.createTextMessage({
        to: groupID,
        conversationType: TIM.TYPES.CONV_GROUP,
        payload: { text: input },
      });

      try {
        const res = await tim.sendMessage(message);
        setMessages((prev) => [...prev, res.data.message]); // <- manually append
        setInput("");
      } catch (err) {
        console.error(`Send message failed: ${groupID}`, err);
        Cookies.remove("user");
        setUser(null);
      }
    }
  };

  return (
    <div className="w-full lg:min-w-lg max-w-lg mx-auto bg-transparent p-3 pt-0 flex flex-col justify-between h-full ">
      {/* Message List */}
      <div
        ref={messageListRef}
        className="flex-1 h-full overflow-y-auto space-y-0.5 mb-4 p-1"
      >
        {messages.map((msg, idx) => {
          const isSystem = msg.from === "@TIM#SYSTEM";
          const timestamp = new Date(msg.time * 1000).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
          if (isSystem) {
            const systemText =
              `${msg.payload.operatorID} joined the chat` || "";
            return (
              <div
                key={msg.ID || idx}
                className="text-center text-xs text-gray-400 my-2"
              >
                [{timestamp}] {systemText}
              </div>
            );
          }

          return (
            <div
              key={msg.ID || idx}
              className="flex flex-col mb-1 items-start text-left overflow-hidden w-full"
            >
              <div className="whitespace-pre-wrap break-words w-fit relative max-w-[100%] pl-3 pr-10 py-2 rounded-lg text-xs text-gray-500 font-semibold mb-1 bg-[#FFCECE] shadow ">
                <div>
                  <span className="text-red-950 font-bold pr-0.5">
                    {msg.from}:
                  </span>
                  <span className=" whitespace-pre-wrap break-words">
                    {msg.payload.text}
                  </span>
                </div>
                <span className="absolute bottom-1 right-2 text-[10px] text-gray-400 mt-1 whitespace-nowrap flex justify-end">
                  {timestamp}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input Box */}
      <div className="flex gap-x-2 items-center">
        <EmojiPicker onSelect={(emoji) => setInput((prev) => prev + emoji)} />
        <form
          onSubmit={(e) => {
            e.preventDefault(); // prevent page reload
            sendMessage();
          }}
          className="flex self-end bg-black/60 rounded-full px-4 py-2 w-full"
        >
          <input
            type="text"
            placeholder="Nhập Tin Nhắn..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 bg-transparent text-white placeholder-gray-400 text-sm outline-none w-full"
          />
          <button type="submit" className="ml-2 transition-colors rounded-full">
            <img
              src={`${process.env.PUBLIC_URL}/img/paper-sent.png`}
              alt="send-button"
              className="w-[24px] h-[24px]"
            />
          </button>
        </form>
      </div>
    </div>
  );
};

export default AvChatRoom;
